<script lang="ts" setup>
import { useRouter } from 'vue-router';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

const props = defineProps<{
  title?: string;
  breadcrumbs?: BreadcrumbItem[];
}>();

const router = useRouter();

function navigateTo(href: string) {
  router.push(href);
}
</script>

<template>
  <div class="page-header">
    <!-- Breadcrumbs -->
    <nav v-if="breadcrumbs && breadcrumbs.length > 0" aria-label="breadcrumb" class="mb-2">
      <ol class="breadcrumb mb-0">
        <li
          v-for="(crumb, index) in breadcrumbs"
          :key="index"
          class="breadcrumb-item"
          :class="{ active: index === breadcrumbs.length - 1 }"
          :aria-current="index === breadcrumbs.length - 1 ? 'page' : undefined"
        >
          <a
            v-if="crumb.href && index !== breadcrumbs.length - 1"
            href="#"
            @click.prevent="navigateTo(crumb.href)"
            style="cursor: pointer;"
          >
            {{ crumb.label }}
          </a>
          <span v-else>{{ crumb.label }}</span>
        </li>
      </ol>
    </nav>

    <!-- Header Row with Title and Actions.
         Desktop (md+): single row — title shrinks/truncates so the actions stay anchored right.
         Mobile: stacks — the title takes the full width and wraps in full (never truncated, since
         it's the page's primary identifier), and the actions drop to their own row, right-aligned
         to mirror their desktop position. -->
    <slot name="header-row">
      <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-md-between gap-2 gap-md-3 mb-3">
        <div class="flex-grow-1 min-w-0">
          <slot name="title">
            <h1 class="mb-0 page-header-title">{{ title }}</h1>
          </slot>
        </div>

        <div class="d-flex gap-2 flex-shrink-0 justify-content-end">
          <slot name="actions">
            <!-- Default content if no actions provided -->
          </slot>
        </div>
      </div>

      <hr />
    </slot>
  </div>



<!--  Alternative mobile/desktop layout example (commented out):-->
<!--  <div class="scheduler-header mb-4">-->
<!--    &lt;!&ndash; Mobile Layout &ndash;&gt;-->
<!--    <div class="d-flex d-md-none justify-content-between align-items-start">-->
<!--      <div class="flex-grow-1">-->
<!--        <h3 class="cards-header mb-2">Scheduled Workflows</h3>-->
<!--        <nav aria-label="breadcrumb">-->
<!--          <ol class="breadcrumb mb-0">-->
<!--            <li class="breadcrumb-item"><a href="#" @click.prevent="router.push('/')">Home</a></li>-->
<!--            <li class="breadcrumb-item active" aria-current="page">Scheduler</li>-->
<!--          </ol>-->
<!--        </nav>-->
<!--      </div>-->
<!--      <div class="d-flex flex-column gap-2 align-items-end flex-shrink-0 ms-3">-->
<!--        <button class="btn btn-outline-primary btn-sm text-nowrap" @click="createNewSchedule">-->
<!--          <i class="bi bi-plus me-1"></i>New-->
<!--        </button>-->
<!--      </div>-->
<!--    </div>-->

<!--    &lt;!&ndash; Desktop Layout &ndash;&gt;-->
<!--    <div class="d-none d-md-flex justify-content-between align-items-center">-->
<!--      <div>-->
<!--        <h3 class="cards-header mb-0">Scheduled Workflows</h3>-->
<!--      </div>-->
<!--      <div class="d-flex align-items-center gap-3">-->
<!--        <nav aria-label="breadcrumb">-->
<!--          <ol class="breadcrumb mb-0">-->
<!--            <li class="breadcrumb-item"><a href="#" @click.prevent="router.push('/')">Home</a></li>-->
<!--            <li class="breadcrumb-item active" aria-current="page">Scheduler</li>-->
<!--          </ol>-->
<!--        </nav>-->
<!--        <div class="d-flex gap-2">-->
<!--          <button class="btn btn-outline-primary text-nowrap" @click="createNewSchedule">-->
<!--            <i class="bi bi-plus me-2"></i>New Schedule-->
<!--          </button>-->
<!--        </div>-->
<!--      </div>-->
<!--    </div>-->
<!--  </div>-->
</template>

<style scoped lang="scss">
@import 'bootstrap/scss/functions';
@import 'bootstrap/scss/variables';
@import 'bootstrap/scss/mixins';

.page-header {
  // Additional styling can be added here if needed
}

// On mobile the title takes the full width and wraps in full (it's the page's primary identifier,
// so it's never clipped). From md up — where the title and actions share a row — truncate instead,
// keeping the actions anchored right.
.page-header-title {
  @include media-breakpoint-up(md) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>


